import { useRef, useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { genUUIDV4 } from '@frontend/string';
import { Modal, TextField, useFormField } from '@frontend/design-system';
import { MAX_TYPED_SIGNATURE_LENGTH } from '../../signature/constants';
import { SignatureDrawArea } from '../../signature/signature-draw-area';
import { SignatureHistoryList } from '../../signature/signature-history-list';
import { SignaturePadHandle } from '../../signature/signature-pad';
import { signatureModalFooterStyle, signatureModalStyle } from '../../signature/signature.styles';
import { SavedPdfSignature, useDynamicFormStore } from '../../store';
import { signatureModalBodyStyle } from './pdf-field.styles';

const PAD_PEN_WIDTH = 3;
const TRIM_PADDING = 4;
const MODAL_WIDTH = 520;

interface PdfSignatureModalProps {
  show: boolean;
  fieldKey: string;
  onSave: (signature: SavedPdfSignature) => void;
  onClose: () => void;
}

/**
 * Draws a new PDF signature (or reuses one from this session), stacked over the fullscreen document viewer.
 * `minWidth` = `maxWidth` pins the width: the modal otherwise shrink-wraps its content, and the canvas resizing to
 * fit makes it collapse while opening.
 */
export const PdfSignatureModal = ({ show, fieldKey, onSave, onClose }: PdfSignatureModalProps) => (
  <Modal
    show={show}
    onClose={onClose}
    minWidth={MODAL_WIDTH}
    maxWidth={MODAL_WIDTH}
    disableCloseOnOverlayClick
    css={signatureModalStyle}
  >
    <PdfSignatureModalContent fieldKey={fieldKey} onSave={onSave} onClose={onClose} />
  </Modal>
);

const PdfSignatureModalContent = ({ fieldKey, onSave, onClose }: Omit<PdfSignatureModalProps, 'show'>) => {
  const { t } = useTranslation('digital-forms-experience');
  const { pdfSignatures, addPdfSignature } = useDynamicFormStore(['pdfSignatures', 'addPdfSignature']);
  const padRef = useRef<SignaturePadHandle>(null);
  const [hasDrawing, setHasDrawing] = useState(false);
  const nameField = useFormField({
    type: 'text',
    value: pdfSignatures[0]?.signedBy ?? '',
    transformValue: (value) => (value ?? '').slice(0, MAX_TYPED_SIGNATURE_LENGTH),
  });
  const signedBy = (nameField.value ?? '').trim();

  const handleSave = () => {
    const exported = padRef.current?.exportTrimmed(TRIM_PADDING);
    if (!exported || !signedBy) return;
    const signature: SavedPdfSignature = {
      id: genUUIDV4(),
      dataUrl: exported.canvas.toDataURL('image/png'),
      width: exported.width,
      height: exported.height,
      signedBy,
    };
    addPdfSignature(signature);
    onSave(signature);
  };

  return (
    <>
      <Modal.Header onClose={onClose}>{t('Draw your signature')}</Modal.Header>
      <Modal.Body css={signatureModalBodyStyle}>
        <SignatureDrawArea
          ref={padRef}
          penWidth={PAD_PEN_WIDTH}
          ariaLabel={t('Signature drawing area')}
          onDrawingChange={setHasDrawing}
        />
        <TextField {...nameField} name={`${fieldKey}-signed-by`} label={t('Enter your Full Name')} />
        <SignatureHistoryList
          items={pdfSignatures}
          getKey={(signature) => signature.id}
          getLabel={(signature) =>
            t('Use signature by {{name}}', { name: signature.signedBy, interpolation: { escapeValue: false } })
          }
          renderItem={(signature) => <img src={signature.dataUrl} alt='' />}
          onSelect={onSave}
        />
      </Modal.Body>
      <Modal.Footer
        css={signatureModalFooterStyle}
        secondary={{ label: t('Cancel'), onClick: onClose }}
        primary={{ label: t('Save'), onClick: handleSave, disabled: !hasDrawing || !signedBy }}
      />
    </>
  );
};
